import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { FormField } from './types';
import FieldCard from './FieldCard';
import { CREATE_FORM, UPDATE_FORM, mapToGraphQLEnums } from '~/services';

interface WorkspaceAreaProps {
  formFields: FormField[];
  formName: string;
  formDescription: string;
  onFormNameChange: (name: string) => void;
  onFormDescriptionChange: (description: string) => void;
  onDeleteField: (fieldId: string) => void;
  onMoveFieldUp: (fieldId: string) => void;
  onMoveFieldDown: (fieldId: string) => void;
  onFieldUpdate?: (fieldId: string, updates: Partial<FormField>) => void;
  agentId?: string;
  onFormCreated?: () => void;
  aiCompleted?: boolean;
  onAiCompletedChange?: (aiCompleted: boolean) => void;
  isEditMode?: boolean;
  formId?: string;
  onBackToForms?: () => void;
}

const WorkspaceArea: React.FC<WorkspaceAreaProps> = ({ 
  formFields, 
  formName, 
  formDescription, 
  onFormNameChange, 
  onFormDescriptionChange, 
  onDeleteField,
  onMoveFieldUp,
  onMoveFieldDown,
  onFieldUpdate,
  agentId,
  onFormCreated,
  aiCompleted = true,
  onAiCompletedChange,
  isEditMode = false,
  formId,
  onBackToForms
}) => {
  const { t } = useTranslation('forms');

  // Mutaciones para crear y actualizar formularios
  const [createForm, { loading: isCreating }] = useMutation(CREATE_FORM);
  const [updateForm, { loading: isUpdating }] = useMutation(UPDATE_FORM);

  const isProcessing = isCreating || isUpdating;

  const handleSaveForm = async () => {
    try {
      const formData = {
        agentId,
        name: formName,
        description: formDescription,
        aiCompleted: aiCompleted,
        fields: formFields.map(field => {
          const mappedField = mapToGraphQLEnums(field);
          return {
            ...mappedField,
            // Para atributos, asegurar que se incluya el attributeId
            ...(field.type === 'attribute' && { attributeId: field.attributeId }),
            // Para integraciones API, incluir toda la configuración
            ...(field.type === 'integration' && field.inputType === 'api' && { apiConfig: mappedField.apiConfig })
          };
        })
      };

      console.log('Enviando formulario:', formData);

      let result;
      if (isEditMode && formId) {
        // Modo edición - actualizar formulario existente
        result = await updateForm({
          variables: {
            data: {
              id: formId,
              ...formData
            }
          }
        });

        if (result.data?.update_form?.hasError) {
          alert(`Error al actualizar formulario: ${result.data.update_form.message}`);
        } else {
          alert('Formulario actualizado exitosamente!');
          // En modo edición, no limpiar el formulario, solo navegar de vuelta
          if (onFormCreated) {
            onFormCreated();
          }
        }
      } else {
        // Modo creación - crear nuevo formulario
        result = await createForm({
          variables: {
            data: formData
          }
        });

        if (result.data?.create_form?.hasError) {
          alert(`Error al crear formulario: ${result.data.create_form.message}`);
        } else {
          alert('Formulario creado exitosamente!');
          // Llamar callback para limpiar el formulario
          if (onFormCreated) {
            onFormCreated();
          }
        }
      }
    } catch (error) {
      console.error('Error saving form:', error);
      alert(`Error al ${isEditMode ? 'actualizar' : 'crear'} el formulario. Por favor, inténtalo de nuevo.`);
    }
  };

  const renderFormField = (field: FormField, index: number) => {
    return (
      <FieldCard
        key={field.id}
        field={field}
        index={index}
        totalFields={formFields.length}
        onDelete={onDeleteField}
        onMoveUp={onMoveFieldUp}
        onMoveDown={onMoveFieldDown}
        onFieldUpdate={onFieldUpdate}
      />
    );
  };

  return (
    <div className="p-6 pb-20 lg:pb-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-4">
            {/* Back button */}
            {onBackToForms && (
              <button
                onClick={onBackToForms}
                className="flex items-center justify-center w-10 h-10 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors"
                title="Volver a formularios"
              >
                <ArrowLeftIcon className="h-5 w-5" />
              </button>
            )}
            
            {/* Title and description */}
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                {isEditMode ? t('edit_form_title', 'Editar Formulario') : `${t('buttons.create_form', 'Crear Formulario')}`}
              </h2>
              <p className="text-gray-600 text-sm">
                {isEditMode 
                  ? t('edit_form_description', 'Modifica los campos y configuración de tu formulario') 
                  : t('config.description')
                }
              </p>
            </div>
          </div>
        </div>

        {/* Form Configuration */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="space-y-4">
            {/* Form Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('labels.form_name')}
              </label>
              <input
                type="text"
                value={formName}
                onChange={(e) => onFormNameChange(e.target.value)}
                placeholder={t('placeholders.form_name')}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Form Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('labels.form_description')}
              </label>
              <textarea
                value={formDescription}
                onChange={(e) => onFormDescriptionChange(e.target.value)}
                placeholder={t('placeholders.form_description')}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              />
            </div>

            {/* AI Form Fill Option */}
            <div className="pt-2 border-t border-gray-200">
              <label className="flex items-center space-x-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={aiCompleted}
                  onChange={(e) => onAiCompletedChange?.(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="font-medium">{t('config.ai_fill_label')}</span>
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-6">
                {t('config.ai_fill_description')}
              </p>
            </div>
          </div>
        </div>

        {/* Form Builder Area */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">{t('labels.form_fields')}</h3>
          
          <div className="min-h-96 p-4 rounded-lg border-2 border-dashed border-gray-300">
            {formFields.length === 0 ? (
              <div className="text-center py-16">
                <div className="text-gray-400 mb-4">
                  
                </div>
                <p className="text-gray-500 text-sm">{t('placeholders.drag_fields_here')}</p>
                <p className="text-gray-400 text-xs mt-2 lg:hidden">
                  {t('config.touch_button_hint')}
                </p>
              </div>
            ) : (
              formFields.map((field, index) => renderFormField(field, index))
            )}
          </div>
        </div>

        {/* Save/Update Button */}
        <div className="text-center">
          <button
            onClick={handleSaveForm}
            disabled={!formName || formFields.length === 0 || isProcessing}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
          >
            {isProcessing 
              ? t(isEditMode ? 'loading.updating' : 'loading.creating') 
              : t(isEditMode ? 'buttons.update_form' : 'buttons.create_form')
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkspaceArea;
